<template>

    <div class="w-full mb-4 md:mb-8">
      <div class="pt-6 w-full">
        <p class="mb-2 font-medium text-neutral-700">{{ $t('modal.insurance.advantages') }}</p>
        <i18n-t keypath="modal.insurance.advantages.description" scope="global" tag="p" class="text-sm text-neutral-500">
          <a class="cursor-pointer font-medium" href="https://edocs.chubbtravelinsurance.com/app/documents/alias/PTTKT_PW_PT/download" target="_blank">{{ $t('modal.insurance.footer.info_link') }}</a>
        </i18n-t>
        <p class="mt-4 mb-2 font-medium text-neutral-700">{{ $t('modal.insurance.risks') }}</p>
        <p class="text-sm text-neutral-500">
          {{ $t('modal.insurance.risks.description') }}
        </p>
        <p class="mt-4 mb-2 font-medium text-neutral-700">{{ $t('modal.insurance.subscribe') }}</p>
        <p class="text-sm text-neutral-500">
          {{ $t('modal.insurance.risks.subscribe') }}
        </p>

        <template v-if="quote">
          <hr class="mt-4 mb-5 w-full border-neutral-200">
          <p class="mt-4 mb-2 font-medium text-neutral-700">{{ $t('modal.insurance.premiums') }}</p>

          <div v-for="(item, index) in quote.pricing.individualPricing"
               class="my-2 flex w-full items-start justify-between text-sm text-neutral-900">
                <div class="flex flex-col justify-start gap-y-1 pr-5">
                  <div class="flex gap-x-2 items-start">
                    <p class="text-sm text-neutral-500">{{ checkout.line_items[index].product.ticket.name }}</p>
                  </div>
                </div>
                <div class="text-neutral-500 text-sm">{{ money(item.totalPrice) }}</div>
          </div>

          <div class="flex w-full items-center justify-between font-medium text-base text-neutral-500">
            <span>{{ $t('common.total') }}</span>
            <span>{{ money(quote.pricing.totalPrice) }}</span>
          </div>
        </template>

        <hr class="mt-4 mb-5 w-full border-neutral-200">
        <i18n-t keypath="modal.insurance.risks.footer" scope="global" tag="p" class="text-xs text-neutral-500">
          <a class="cursor-pointer text-neutral-700 font-medium" href="https://edocs.chubbtravelinsurance.com/app/documents/alias/PTTKT_PW_PT/download" target="_blank">{{ $t('modal.insurance.footer.info_link') }}</a>
          <a class="cursor-pointer text-neutral-700 font-medium" href="https://edocs.chubbtravelinsurance.com/app/documents/alias/PTTKT_IPID_PT/download" target="_blank">{{ $t('modal.insurance.footer.contract_info') }}</a>
          <a class="cursor-pointer text-neutral-700 font-medium" href="mailto:clientes.pt@chubb.com" target="_blank">clientes.pt@chubb.com</a>
        </i18n-t>
        <i18n-t keypath="modal.insurance.risks.privacy" scope="global" tag="p" :class="insurance? 'pb-4' : ''" class="mt-4 text-xs text-neutral-500">
          <a class="cursor-pointer text-neutral-700 font-medium" href="https://www.chubb.com/pt-pt/footer/privacy-policy.html" target="_blank">{{ $t('modal.insurance.risks.privacy.policy') }}</a>
        </i18n-t>

        <hr class="mt-4 mb-5 w-full border-neutral-200">
        <UCheckbox v-model="checkbox"
                    :disabled="insurance !== null"
                    :ui="{label: 'text-neutral-500 font-normal cursor-pointer'}"
                    :label="$t('modal.insurance.checkbox')"
                    :class="checkbox ? 'pb-4' : ''"
                    input-class="h-5 w-5 rounded-md border border-neutral-300 checked:bg-neutral-900 checked:focus:bg-neutral-900 checked:hover:bg-neutral-900">
        </UCheckbox>

        <UFormGroup ref="nifFormRef" v-if="checkbox" :error="$t(nifError)" :label="$t('modal.insurance.step2.nif')" name="insuranceNif" class="!ml-8 no-ring-form" required>
          <UInput
              size="lg"
              type="text"
              inputmode="numeric"
              v-model="insuranceNif"
              @beforeinput="handleBeforeInput"
              @update:model-value="onNifInput"
              @keyup.enter="triggerEnterNif"
              class="!mt-2 w-full bg-white"
              :placeholder="$t('modal.insurance.step2.nif')">
          </UInput>
        </UFormGroup>
      </div>
    </div>

    <!-- INSURANCE BUTTONS -->
  <template v-if="!insurance">
    <div class="w-full pt-4 md:relative md:py-0 md:px-0">
      <div class="flex items-center justify-between gap-x-3">
        <BaseButton type="neutral"
                    :disabled="(!checkbox || (!isValidNif && checkbox)) || modelValue.loading"
                    :loading="modelValue.loading" @click="addButton">
          <span v-if="!insurance" class="text-base">{{ $t('common.add') }}  {{ $t('common.for') }} {{ money(quote?.pricing.totalPrice) }}</span>
        </BaseButton>
      </div>
    </div>
  </template>
  <template v-if="insurance">
    <div class="w-full pt-4 md:relative md:py-0 md:px-0">
      <div class="flex items-center justify-between gap-x-3">
        <BaseButton type="red-secondary" 
                    :disabled="modelValue.loading"
                    @click="$emit('cancelInsurance')" 
                    class="!bg-transparent w-1/2">
          <span class="text-base">{{ $t('modal.insurance.cancel.remove_insurance') }}</span>
        </BaseButton>
        <BaseButton type="neutral" 
                    :loading="modelValue.loading" 
                    :disabled="!hasNifChanged || modelValue.loading" 
                    class="w-1/2" 
                    @click="addButton">
          {{ $t('common.edit') }} NIF
        </BaseButton>
      </div>
    </div>
  </template>
</template>

<script lang="ts" setup>
import {useMoneyFormatter} from "~/composables/moneyFormatter"
import {useCheckoutStore} from "~/store/checkout"
import type {CheckoutApi} from "~/utils/types/checkout.d"

const money = useMoneyFormatter()
const checkout: CheckoutApi = useCheckoutStore()

const props = defineProps({
  insurance: Object,
  quote: Object,
})

const emit = defineEmits(['addInsurance', 'cancelInsurance'])
const modelValue = useAttrs().modelValue as ModalModel
modelValue.icon = "shield"
modelValue.title = "common.insurance.title"
modelValue.subtitle = "modal.insurance.description"

const checkbox = ref(false)
const insuranceNif = ref('')
const nifError = ref('')
const nifFormRef = ref();

const isValidNif = computed(() => (/^[123][0-9]{8}$/.test(insuranceNif.value)))

watchEffect(() => {
  if (props.insurance) {
    checkbox.value = true;
    insuranceNif.value = props.insurance.attributes.quotation_nif || ''
  } else {
    insuranceNif.value = '';
    checkbox.value = false;
  }
})

const hasNifChanged = computed(() => {
  if(!props.insurance) return false;
  const originalNif = props.insurance.attributes.quotation_nif || '';
  return (insuranceNif.value !== originalNif) && isValidNif.value;
})

watch(checkbox, (checked) => {
  if(checked) {
    nextTick(() => {
      if (nifFormRef.value) {
        nifFormRef.value.$el.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        })
      }
    })
  }
})

function handleBeforeInput(event: InputEvent): void {
 if (event.data && !/\d/.test(event.data)) {
   event.preventDefault()
 }
}

function onNifInput(value: string) {
  if (!/^[123][0-9]{0,8}$/.test(value)) {
    nifError.value = 'modal.insurance.step2.nif.error'
  } else {
    nifError.value = ''
  }
}

function addButton() {
  return emit('addInsurance', insuranceNif.value)
}

function triggerEnterNif() {
  if (isValidNif.value) {
    addButton();
  }
}
</script>